import { useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

// local imports - types
import warningImage from 'assets/images/warning-image.svg'
import calendarLoading from 'assets/icons/loading-calendar.png'
import DateUtil from 'utils/DateUtil'
import { IFilteredMeetingRoomList } from 'types/MeetingRoomType'
import Loader from 'components/ui/Loader'
import WeekSlider from 'components/home/on-campus-services/health-service/appointment-scheduler/WeekSlider'
import ScheduleSidebar from 'components/home/on-campus-services/meeting-room/reservation-scheduler/ScheduleSidebar'
import Scheduler from 'components/home/on-campus-services/meeting-room/reservation-scheduler/Scheduler'
import ReservationModalHeader from 'components/home/on-campus-services/meeting-room/reservation-scheduler/ReservationModalHeader'
import NotificationDialog from 'components/ui/NotificationDialog'
import {
  ActionArea,
  CancelButton,
  ConfirmButton,
} from 'components/home/on-campus-services/coiffeur/reservation-scheduler/styled'
import useGetMeetingRoomResTable from 'components/home/on-campus-services/meeting-room/hooks/use-get-meeting-room-res-table'

/* Types */
interface IModalFooterProps {
  disabled: boolean
  onCancel: () => void
  onSave: () => void
}

interface IAppointmentModalProps {
  cancelReservationModal: () => void
  filterDate: Date
  selectedRoom: IFilteredMeetingRoomList
  open: boolean
  closeModal: () => void
  openModal: () => void
  showNotification: () => void
  selectedDateStr?: string
}

export default function MeetingResModalWrapper({
  cancelReservationModal,
  filterDate: dateFromList,
  selectedRoom,
  open,
  closeModal,
  openModal,
  showNotification,
  selectedDateStr,
}: IAppointmentModalProps) {
  const { t } = useTranslation()

  /* Meeting Room - reservationDate filter value for appointment date filtering etc. */
  const [filterDate, setFilterDate] = useState<Date>(dateFromList)
  const lastFilteredDate = DateUtil.getWeekStartEndFromDate(dateFromList)
  const [diffDayNotification, setDiffDayNotification] = useState(false)
  const isSelectedDifferentDate = moment(filterDate).isSame(dateFromList, 'day')

  /* GET Selected Meeting Room's Reservation Table List | Custom Hook  */
  const roomLocationId = selectedRoom?.floor?.locationId
  const meetingRoomId = selectedRoom?.id
  const {
    availableDates,
    tableWeek,
    setTableWeek,
    selectedReservations,
    setSelectedReservations,
    sidebarVisible,
    setSidebarVisible,
    loading,
    dailyAppointments,
  } = useGetMeetingRoomResTable(roomLocationId, meetingRoomId, dateFromList, true)

  /* Open Reservation Sidebar */
  const openReservationSidebar = () => {
    if (isSelectedDifferentDate) {
      setSidebarVisible(true)
    } else {
      setDiffDayNotification(true)
    }
    closeModal()
  }
  /* Close Sidebar and reset states */
  const closeSidebarAfterSave = () => {
    showNotification()
    setSidebarVisible(false)
    closeModal()
  }

  /* Close with cancel */
  const handleCloseOnCancel = () => {
    cancelReservationModal()
    setFilterDate(dateFromList)
    setSelectedReservations(undefined)
    setTableWeek(lastFilteredDate)
  }

  /* Different Day Reservation - Notification Dialog Methods */
  const handleAcceptDiffDayReservation = () => {
    setDiffDayNotification(false)
    setSidebarVisible(true)
    closeModal()
  }
  const handleRejectDiffDayReservation = () => {
    setDiffDayNotification(false)
    openModal()
  }

  return (
    <>
      <Dialog
        id="appointmentModal"
        blockScroll
        keepInViewport
        closeOnEscape={false}
        closable={true}
        focusOnShow={false}
        header={<span>{`${t('meeting-res-modal.meeting-reservation')}`} (2/2)</span>}
        draggable={false}
        resizable={false}
        visible={open}
        style={{ width: '95vw', height: '95vh' }}
        breakpoints={{ '1536px': '95vw', '1280': '95vw' }}
        onHide={handleCloseOnCancel}
        footer={
          <FooterComponent
            onSave={openReservationSidebar}
            onCancel={handleCloseOnCancel}
            disabled={!!selectedReservations && selectedReservations?.length < 1}
          />
        }>
        {/* Modal Header */}
        <ReservationModalHeader
          selectedDateStr={selectedDateStr}
          filterDate={filterDate}
          availableDateRange={availableDates}
          setFilterDate={setFilterDate}
          room={selectedRoom}
          setTableWeek={setTableWeek}
        />

        {/* Week Change Slider */}
        <WeekSlider availableDateRange={availableDates} tableWeek={tableWeek} setTableWeek={setTableWeek} />

        {/* Content - Calendar View */}
        {loading ? (
          <Loader src={calendarLoading} />
        ) : (
          <Scheduler
            filterDate={filterDate}
            setFilterDate={setFilterDate}
            tableWeek={tableWeek}
            dailyAppointments={dailyAppointments}
            selectedReservations={selectedReservations}
            setSelectedReservations={setSelectedReservations}
          />
        )}
      </Dialog>

      {/* Schedule - Save Appointment Sidebar */}
      {!!selectedReservations && selectedReservations?.length > 0 && (
        <ScheduleSidebar
          openModal={openModal}
          visible={sidebarVisible}
          setVisible={setSidebarVisible}
          closeAfterSave={closeSidebarAfterSave}
          roomInfo={selectedRoom}
          reservationInfos={selectedReservations}
        />
      )}

      <NotificationDialog
        display={diffDayNotification}
        imgSrc={warningImage}
        goHomeButton={true}
        extraButton={true}
        title={t('overlaypanel-texts.reservation-day-changed')}
        content={t('overlaypanel-texts.reservation-day-change-text')}
        firstButtonLabel={t('buttons.yes')}
        secondButtonLabel={t('buttons.no')}
        onHide={handleRejectDiffDayReservation}
        onSubmit={handleAcceptDiffDayReservation}
        selectedType={null}
        setSelectedType={undefined}
        types={[]}
      />
    </>
  )
}

function FooterComponent({ onSave, onCancel, disabled }: IModalFooterProps) {
  const { t } = useTranslation()
  return (
    <ActionArea>
      <CancelButton id="closeModal" className="p-button-outlined" label={t('buttons.cancel')} onClick={onCancel} />
      <ConfirmButton id="openSidebar" disabled={disabled} label={t('buttons.book-reservation')} onClick={onSave} />
    </ActionArea>
  )
}
